import styles from './HorizontalTabs.module.css'
import { BasicStyledComponent } from 'shared-definitions/types'
import clsx from 'clsx'
import { CSSProperties, ReactElement, useEffect, useState } from 'react'
import { useElementResize } from '../hooks/use-element-resize'

export interface HorizontalTabsProps<T> {
  tabs: {
    label: string
    key: T
  }[]
  currentTabKey: T
  onTabChange: (tabKey: T) => void
}

interface MovingBgPosition {
  left: number
  top: number
  width: number
}

const HorizontalTabs = <T extends string | number>({
  className,
  tabs,
  onTabChange,
  currentTabKey,
}: HorizontalTabsProps<T> & BasicStyledComponent): ReactElement => {
  const [movingBgPosition, setMovingBgPosition] = useState<MovingBgPosition>({
    left: 0,
    top: 0,
    width: 0,
  })

  function calculateMovingBgPosition(container: HTMLDivElement): MovingBgPosition {
    const activeButton = container.querySelector<HTMLButtonElement>(`.${styles.active}`)

    if (!activeButton) {
      return {
        left: 0,
        top: 0,
        width: 0,
      }
    }

    return {
      left: activeButton.offsetLeft,
      top: activeButton.offsetTop,
      width: activeButton.offsetWidth,
    }
  }

  const ref = useElementResize<HTMLDivElement>(container => {
    setMovingBgPosition(calculateMovingBgPosition(container))
  })
  useEffect(() => {
    if (ref.current) {
      setMovingBgPosition(calculateMovingBgPosition(ref.current))
    }
  }, [currentTabKey])

  return (
    <div
      className={clsx(styles.container, className)}
      ref={ref}
      style={
        {
          '--moving-bg-left': `${movingBgPosition.left}px`,
          '--moving-bg-top': `${movingBgPosition.top}px`,
          '--moving-bg-width': `${movingBgPosition.width}px`,
        } as CSSProperties
      }
    >
      <div className={styles.movingBg} />
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          className={clsx(styles.tab, {
            [styles.active]: tab.key === currentTabKey,
          })}
          onClick={() => onTabChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

export default HorizontalTabs
